/**
 * Pure helpers for the read-only bank-notification list.
 * No React/DOM — testable from src/__tests__/bankNotifications.test.ts.
 */
import { formatVND } from "../utils";

/** Số tiền VND; "—" khi server không đọc được amount (null/undefined/không hữu hạn). */
export function formatNotificationAmount(amount: number | null | undefined): string {
  if (amount == null || !Number.isFinite(amount)) return "—";
  return formatVND(amount);
}

/* ── Quy tắc đọc số tiền theo app ───────────────────────────────── */

/** Trần skip_count theo contract API (0-5). */
export const RULE_SKIP_MAX = 5;

export type BankNotifyRuleForm = {
  anchor: string;
  skip_count: number;
  require_sign: boolean;
  speak: boolean;
};

const REASON_LABELS: Record<string, string> = {
  OK: "Đọc được",
  ANCHOR_NOT_FOUND: "Không thấy mốc chữ",
  NO_TOKEN_AFTER_ANCHOR: "Không có số sau mốc",
  SKIPPED_ALL: "Bỏ qua hết số ứng viên",
  OUTGOING: "Giao dịch ghi nợ (bỏ qua)",
};

/** Nhãn tiếng Việt cho reason code của preview; code lạ hiển thị nguyên văn. */
export function ruleReasonLabel(code: string | null | undefined): string {
  if (!code) return "Không rõ";
  return REASON_LABELS[code] ?? code;
}

/** Đếm số tin đọc được / không đọc được trong preview. */
export function summarizePreview(
  samples: Array<{ amount_vnd: number | null | undefined }> | null | undefined,
): { ok: number; nulls: number } {
  let ok = 0;
  let nulls = 0;
  for (const s of samples ?? []) {
    if (s.amount_vnd == null || !Number.isFinite(s.amount_vnd)) nulls += 1;
    else ok += 1;
  }
  return { ok, nulls };
}

/** Chuẩn hoá form → patch gửi API: trim anchor, kẹp skip 0-5, ép boolean. */
export function buildRulePatch(form: BankNotifyRuleForm): BankNotifyRuleForm {
  const n = Number.isFinite(form.skip_count) ? Math.trunc(form.skip_count) : 0;
  return {
    anchor: (form.anchor ?? "").trim(),
    skip_count: Math.min(RULE_SKIP_MAX, Math.max(0, n)),
    require_sign: form.require_sign === true,
    speak: form.speak === true,
  };
}

/* ── Lọc app "rác" ──────────────────────────────────────────────── */

/**
 * App rác = dữ liệu self-test khi deploy hoặc MacroDroid gửi biến chưa điền.
 * Cố tình khớp hẹp để không ẩn nhầm app ngân hàng thật
 * (com.vcb.sme, com.techcombank.notiapp → false: có dấu chấm, không khớp mẫu test).
 */
export function isJunkAppPackage(pkg: string | null | undefined): boolean {
  const p = (pkg ?? "").trim().toLowerCase();
  if (!p) return true; // ô trống
  if (p.startsWith("{")) return true; // "{not_app_package}" — biến MacroDroid chưa điền
  if (p.includes("not_app_package")) return true;
  if (p.startsWith("test.") || p.startsWith("test app package") || p.startsWith("selftest")) return true;
  return !p.includes("."); // package Android hợp lệ luôn có dấu chấm
}

/** Ẩn app rác, trừ khi người dùng đã lưu quy tắc cho nó (hasRule) — khi đó họ cố ý cấu hình. */
export function shouldHideApp(pkg: string | null | undefined, hasRule: boolean): boolean {
  return !hasRule && isJunkAppPackage(pkg);
}

export type BankNotificationQuery = { limit?: number; offset?: number; app_package?: string };

/** Query string đúng convention api.ts: bỏ field rỗng, app_package được trim. */
export function buildBankNotificationQuery(params: BankNotificationQuery = {}): string {
  const sp = new URLSearchParams();
  if (params.limit) sp.set("limit", String(params.limit));
  if (params.offset) sp.set("offset", String(params.offset));
  const pkg = params.app_package?.trim();
  if (pkg) sp.set("app_package", pkg);
  return sp.toString();
}
